import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Capacitor } from "@capacitor/core";

import { patternToTimeline, play, encode as encodeMorse } from "@nav-games/morse";
import { Listener } from "@nav-games/multiplayer-ble";
import { buildPoseSequence } from "@nav-games/semaphore";

import { Flag } from "../ui/Flag";
import { SemaphoreFigure } from "../ui/SemaphoreFigure";

interface BroadcastListenProps {
  onNavigate: (to: string) => void;
}

interface Envelope {
  module: string;
  text: string;
}

type State =
  | { kind: "idle" }
  | { kind: "joining" }
  | { kind: "listening"; received: Envelope | null }
  | { kind: "ended"; reason: string };

/// Module-agnostic listener page. Joins a broadcaster's session and
/// renders whatever it sends — switching renderers on the fly when
/// the broadcaster moves between Morse, Semaphore, Flags. Designed
/// to be the single place a student opens to follow their
/// instructor across an entire lesson.
export function BroadcastListen({ onNavigate }: BroadcastListenProps) {
  const native = Capacitor.isNativePlatform();
  const [state, setState] = useState<State>({ kind: "idle" });
  const listenerRef = useRef<Listener<Envelope> | null>(null);

  useEffect(() => {
    return () => {
      const l = listenerRef.current;
      if (l) void l.stop();
      listenerRef.current = null;
    };
  }, []);

  const join = useCallback(async () => {
    if (!native) return;
    setState({ kind: "joining" });
    const listener = new Listener<Envelope>();
    listenerRef.current = listener;
    try {
      await listener.start({
        onOpen: () => {
          setState((s) => (s.kind === "joining" ? { kind: "listening", received: null } : s));
        },
        onMessage: (env) => {
          setState({ kind: "listening", received: env });
        },
        onClose: (reason) => {
          setState({ kind: "ended", reason: reason ?? "Broadcast ended." });
        },
        onError: (err) => {
          setState({ kind: "ended", reason: err.message });
        },
      });
    } catch (e) {
      setState({ kind: "ended", reason: (e as Error).message });
    }
  }, [native]);

  if (!native) {
    return (
      <div className="card">
        <h2>Listen to a broadcast</h2>
        <p className="muted">
          Joining a Bluetooth broadcast needs the mobile app — install
          the iOS or Android build and open this page on the same goastern
          account.
        </p>
        <button className="secondary" onClick={() => onNavigate("/play")}>
          ← Back to Play
        </button>
      </div>
    );
  }

  switch (state.kind) {
    case "idle":
      return (
        <>
          <h2>Listen to a broadcast</h2>
          <p className="muted">
            Looking for an instructor's broadcast nearby. Phones must be on,
            with Bluetooth enabled, and within ~10 m.
          </p>
          <button onClick={join}>Start scanning</button>
          <p style={{ marginTop: 16 }}>
            <button className="secondary" onClick={() => onNavigate("/play")}>
              ← Back to Play
            </button>
          </p>
        </>
      );
    case "joining":
      return (
        <div className="card">
          <h2>Looking for a broadcaster…</h2>
          <p className="muted">
            Keep your phone near the instructor's. Make sure they've started
            a broadcast on their phone.
          </p>
        </div>
      );
    case "listening":
      return <Listening envelope={state.received} onLeave={() => onNavigate("/play")} />;
    case "ended":
      return (
        <div className="card">
          <h2>Broadcast ended</h2>
          <p className="muted">{state.reason}</p>
          <button onClick={join}>Try again</button>
          <p style={{ marginTop: 8 }}>
            <button className="secondary" onClick={() => onNavigate("/play")}>
              ← Back to Play
            </button>
          </p>
        </div>
      );
  }
}

function Listening({
  envelope,
  onLeave,
}: {
  envelope: Envelope | null;
  onLeave: () => void;
}) {
  return (
    <>
      <p className="small muted">📡 Connected to instructor</p>
      <div className="card" style={{ textAlign: "center" }}>
        {envelope === null ? (
          <p className="muted">Waiting for the instructor to send a message…</p>
        ) : (
          <RenderEnvelope envelope={envelope} />
        )}
      </div>
      <button className="secondary" onClick={onLeave}>
        Leave broadcast
      </button>
    </>
  );
}

function RenderEnvelope({ envelope }: { envelope: Envelope }) {
  switch (envelope.module) {
    case "morse":
      return <MorseRender text={envelope.text} />;
    case "semaphore":
      return <SemaphoreRender text={envelope.text} />;
    case "flags":
      return <FlagsRender text={envelope.text} />;
    default:
      return (
        <p className="muted">
          Unknown module "{envelope.module}". Update the app to receive this.
        </p>
      );
  }
}

function MorseRender({ text }: { text: string }) {
  const morse = useMemo(() => encodeMorse(text), [text]);
  const timeline = useMemo(() => patternToTimeline(morse, 12), [morse]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const onPlayTone = useCallback(() => {
    if (timeline.length === 0) return;
    if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
    play(timeline, { audioContext: audioCtxRef.current });
  }, [timeline]);
  return (
    <>
      <p className="small muted">Morse</p>
      <div
        style={{
          fontFamily: "ui-monospace, monospace",
          fontSize: 22,
          letterSpacing: "0.1em",
          margin: "12px 0 16px",
          wordBreak: "break-all",
          minHeight: "1.4em",
        }}
      >
        {morse.replace(/\./g, "•").replace(/-/g, "—") || (
          <span className="muted">—</span>
        )}
      </div>
      <button onClick={onPlayTone} disabled={timeline.length === 0}>
        ▶ Play tone
      </button>
    </>
  );
}

function SemaphoreRender({ text }: { text: string }) {
  const frames = useMemo(() => buildPoseSequence(text), [text]);
  const [cursor, setCursor] = useState(0);
  useEffect(() => {
    setCursor(0);
  }, [text]);
  useEffect(() => {
    if (frames.length === 0) return;
    const t = setInterval(() => {
      setCursor((c) => (c + 1) % frames.length);
    }, 1200);
    return () => clearInterval(t);
  }, [frames]);
  const frame = frames[cursor] ?? null;
  const pose = frame && frame.kind !== "rest" ? frame.pose : null;
  const caption =
    frame?.kind === "numericPrefix"
      ? "Numbers follow…"
      : frame?.kind === "digit"
        ? "(number)"
        : frames.length > 0
          ? `Step ${cursor + 1} of ${frames.length}`
          : "—";
  return (
    <>
      <p className="small muted">Semaphore</p>
      <SemaphoreFigure pose={pose} size={200} />
      <p className="small muted" style={{ minHeight: "1.2em" }}>
        {caption}
      </p>
    </>
  );
}

function FlagsRender({ text }: { text: string }) {
  const seq = useMemo(
    () =>
      text
        .toUpperCase()
        .split("")
        .filter((c) => c !== " "),
    [text],
  );
  return (
    <>
      <p className="small muted">Maritime signal flags</p>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          justifyContent: "center",
          margin: "8px 0",
        }}
      >
        {seq.map((c, i) => (
          <Flag key={`${i}-${c}`} char={c} size={88} />
        ))}
      </div>
      {seq.length === 0 && <p className="muted">—</p>}
    </>
  );
}
